import deMessages from '@locales/de.json'
import enMessages from '@locales/en.json'
import type { ReactNode } from 'react';
import { createContext, useContext, useState } from 'react'
import { IntlProvider } from 'react-intl'

import type { I18nContextValue, SupportedLocale } from './I18nContext.types'

const messages = {
  en: enMessages,
  de: deMessages,
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined)

export interface I18nProviderProps {
  children: ReactNode
}

export const I18nProvider = ({ children }: I18nProviderProps) => {
  // Initialize locale from saved preference or browser language
  const getInitialLocale = (): SupportedLocale => {
    const savedLang = localStorage.getItem('locale') as SupportedLocale | null
    if (savedLang) {
      return savedLang
    }
    const browserLang = navigator.language.split('-')[0]
    return browserLang === 'de' ? 'de' : 'en'
  }

  const [locale, setLocale] = useState<SupportedLocale>(() => getInitialLocale())

  const changeLocale = (newLocale: SupportedLocale) => {
    setLocale(newLocale)
    localStorage.setItem('locale', newLocale)
  }

  const contextValue: I18nContextValue = {
    locale,
    changeLocale,
  }

  return (
    <I18nContext.Provider value={contextValue}>
      <IntlProvider locale={locale} messages={messages[locale]} defaultLocale="en">
        {children}
      </IntlProvider>
    </I18nContext.Provider>
  )
}

export const useI18n = (): I18nContextValue => {
  const context = useContext(I18nContext)
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider')
  }
  return context
}
